import { IframeHTMLAttributes, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useMobileNavigationDispatch } from '@legacy/stores/MobileNavigationStore';
import cx from 'classnames';
import { shortcutElementNames } from '@legacy/components/global/ShortcutSystem';
import { REPORTING_WIDGET_HAS_ACCESS_KEY } from '@legacy/apis/reportingWidgetsAPI/get/all/getAllAPI';
import { LoadingSpinner } from '@legacy/components/ui-components/LoadingSpinner';
import { useGetFrontendAddressUrl } from '@legacy/apis/indexAPI/getIndexAPI';
import styles from './LegacyPage.module.scss';
import { useSearchStore } from '@/stores/search';
import { useHasNewNavigation, useHasNewSearchUI } from '@/hooks/featureFlags';
import {
  cleanIframeMarkers,
  generateIframeKey,
  generateIframeUrl,
} from './helpers';
import { PageTitle } from '@/components/global/PageTitle';
import { tlpBase, tlpOrigin } from '@/utils/gateway';
import { ensureFreshAuth } from '@/auth/ensureFreshAuth';

interface LegacyPageProps extends IframeHTMLAttributes<HTMLIFrameElement> {
  'data-automation-id': string;
  id?: string;
  name?: string;
  src?: string;
  isInModal?: boolean;
  isNestedInNewPage?: boolean;
}

export const LegacyPage = ({
  src,
  id = 'timelog-legacy-page-iframe',
  name = 'timelog-legacy-page-iframe',
  isInModal,
  isNestedInNewPage,
  ...props
}: LegacyPageProps) => {
  const { updateStore } = useSearchStore();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();

  const hasNewNavigation = useHasNewNavigation();
  const hasNewSearchUi = useHasNewSearchUI();
  const hasNewSearchUiEnabled = hasNewSearchUi && hasNewNavigation;

  const customerSiteUrl = tlpBase();
  const { frontendAddressUrl, isSuccess: frontendAddressUrlIsSuccess } =
    useGetFrontendAddressUrl();

  const [isLoading, setIsLoading] = useState(true);
  const [pageTitle, setPageTitle] = useState('TimeLog - PSA');
  const [iframeKey, setIFrameKey] = useState(0);
  const [iframeHeight, setIframeHeight] = useState<number | null>(null);
  const iframeHeightRef = useRef<number | null>(null);

  const [iframeUrl, setIframeUrl] = useState('');

  const mobileNavigationDispatch = useMobileNavigationDispatch();

  useEffect(() => {
    // Reset the focus state in the navigation after redirect to iframe url
    if (document.activeElement && !hasNewNavigation) {
      (document.activeElement as HTMLElement).blur();
    }

    // Close the mobile navigation before redirecting to TLP page
    if (mobileNavigationDispatch) {
      mobileNavigationDispatch({
        type: 'CLOSED',
      });
    }

    document.body.classList.add(styles.IsLegacy);

    return function cleanup() {
      document.body.classList.remove(styles.IsLegacy);
    };
  }, [mobileNavigationDispatch, iframeUrl]);

  useEffect(() => {
    let cancelled = false;
    const nextUrl = generateIframeUrl(
      customerSiteUrl,
      pathname,
      search,
      src,
      isNestedInNewPage,
    );

    setIsLoading(true);
    (async () => {
      const authed = await ensureFreshAuth();
      if (cancelled || !authed) return;
      setIframeUrl(nextUrl);
    })();

    return () => {
      cancelled = true;
    };
  }, [customerSiteUrl, pathname, search, src, isNestedInNewPage]);

  const setLegacySearchUiFocus = () => {
    const mainSearchInput = document.getElementsByName(
      shortcutElementNames.mainSearch,
    );

    // main search input
    if (mainSearchInput.length > 0) {
      // Add an arbitrary timeout to ensure the element is rendered
      setTimeout(() => {
        mainSearchInput[0].focus();
      }, 100);
    }
  };

  useEffect(() => {
    const handleIframeEvent = (event: MessageEvent) => {
      const {
        data: { eventId, url, title, flagValue },
      } = event;

      // If the site name is in uppercase, convert it to lowercase
      // so that the comparison is case-insensitive
      const lowerCaseUrl = url?.toLowerCase();
      const lowerCaseCustomerSiteUrl = customerSiteUrl?.toLowerCase();
      const frontendAddressUrlLowerCase = frontendAddressUrl?.toLowerCase();
      const iframeUrlLowerCase = iframeUrl?.toLowerCase();

      switch (eventId) {
        case 'urlClicked': {
          /*
           * If is redirect to external url, use window.location.href
           */
          if (
            url.startsWith('http') &&
            !lowerCaseUrl.startsWith(lowerCaseCustomerSiteUrl) &&
            !lowerCaseUrl.startsWith(frontendAddressUrlLowerCase)
          ) {
            window.location.href = url;
          } else if (
            frontendAddressUrlIsSuccess &&
            lowerCaseUrl.startsWith(frontendAddressUrlLowerCase)
          ) {
            setIsLoading(true);
            navigate(url.replace(frontendAddressUrl, ''));
            setIFrameKey(new Date().valueOf());
          } else {
            setIsLoading(true);
            // Case-insensitive replacement of customerSiteUrl
            const regex = new RegExp(
              `^${customerSiteUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`,
              'i',
            );
            const navigateUrl = url.replace(regex, '');
            navigate(navigateUrl);
            setIFrameKey(new Date().valueOf());
          }
          break;
        }
        case 'reloadSession': {
          window.location.href = decodeURIComponent(url);
          break;
        }
        case 'iframeUrlUpdated': {
          if (
            lowerCaseUrl !== iframeUrlLowerCase &&
            lowerCaseUrl.startsWith(lowerCaseCustomerSiteUrl)
          ) {
            // Escape regex metacharacters in customerSiteUrl before using in RegExp
            // to ensure characters like '.' in URLs are treated literally, not as regex tokens
            const urlWithoutCustomerSite = url.replace(
              new RegExp(
                `^${customerSiteUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`,
                'i',
              ),
              '',
            );
            const cleanedUrl = cleanIframeMarkers(urlWithoutCustomerSite);

            // Check if this is a problematic URL that needs full React Router sync
            const needsFullSync = pathname
              .toLowerCase()
              .includes('/newinvoicewithproject/');

            if (needsFullSync) {
              // Full sync with React Router to prevent duplicate actions
              navigate(cleanedUrl, { replace: true });
            } else {
              // Just update browser URL without triggering React Router
              window.history.replaceState(window.history.state, '', cleanedUrl);
            }
          }
          break;
        }
        case 'title': {
          setPageTitle(title);
          break;
        }
        case 'cmdKShortcut': {
          updateStore({
            type: 'TOGGLE_SEARCH',
          });

          if (!hasNewSearchUiEnabled) {
            // If the new search UI is not used, we just treat cmd+K as an alias for the F2 shortcut, so we need to set focus into the legacy search input
            setLegacySearchUiFocus();
          }
          break;
        }
        case 'f2Shortcut': {
          updateStore({
            type: 'TOGGLE_SEARCH',
          });

          setLegacySearchUiFocus();
          break;
        }
        case 'reportingManagerUpdated': {
          localStorage.setItem(REPORTING_WIDGET_HAS_ACCESS_KEY, flagValue);
          break;
        }
        case 'iframe-height': {
          if (isNestedInNewPage && event.data) {
            const { height, fromModal } = event.data;
            const parsedHeight =
              typeof height === 'number' ? height : Number(height);

            // Validate height is a finite number within acceptable bounds
            // Max height of 50000px to prevent absurdly large values
            const isValidHeight =
              Number.isFinite(parsedHeight) &&
              parsedHeight > 0 &&
              parsedHeight <= 50000;

            if (!isValidHeight) {
              break;
            }

            // For modal updates, prevent shrinking and add extra space
            if (fromModal) {
              const adjustedHeight = Math.min(parsedHeight + 50, 50000);
              const currentHeight = iframeHeightRef.current;
              if (currentHeight && adjustedHeight <= currentHeight) {
                break; // Skip update if modal height would shrink
              }
              setIframeHeight(adjustedHeight);
              iframeHeightRef.current = adjustedHeight;
            } else {
              setIframeHeight(parsedHeight);
              iframeHeightRef.current = parsedHeight;
            }
          }
          break;
        }
        case 'refresh-page': {
          window.location.reload();
          break;
        }
        case 'unauthorized': {
          (async () => {
            const authed = await ensureFreshAuth();
            if (authed) setIFrameKey(Date.now());
          })();
          break;
        }
        default:
          break;
      }
    };

    window.addEventListener('message', handleIframeEvent as EventListener);

    return () => {
      window.removeEventListener('message', handleIframeEvent as EventListener);
    };
  }, [
    navigate,
    iframeUrl,
    setPageTitle,
    customerSiteUrl,
    pathname,
    search,
    mobileNavigationDispatch,
    frontendAddressUrl,
    frontendAddressUrlIsSuccess,
    isNestedInNewPage,
    updateStore,
    hasNewSearchUiEnabled,
  ]);

  // This is for not auto focusing inputs inside iframe
  // if the main search is opened when iframe loads
  // which were causing the focus in search input to be lost

  // This is for handling Safari browser onload firing multiple times
  const hasPostedRef = useRef(false);

  useEffect(() => {
    hasPostedRef.current = false;
  }, [iframeUrl]);

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const { isMainSearchOpen } = useSearchStore();

  const handleIframeLoad = () => {
    if (hasPostedRef.current) return;
    hasPostedRef.current = true;
    const targetOrigin = tlpOrigin();

    if (iframeRef.current?.contentWindow && targetOrigin) {
      iframeRef.current.contentWindow.postMessage(
        { eventId: 'mainSearchTriggered', isOpen: isMainSearchOpen },
        targetOrigin,
      );
    }

    setIsLoading(false);
  };

  const content = (
    <>
      <PageTitle title={pageTitle} fromLegacyPage={true} />
      {iframeUrl && (
        <iframe
          ref={iframeRef}
          loading="lazy"
          key={generateIframeKey(iframeUrl, iframeKey)}
          id={id}
          name={name}
          src={iframeUrl}
          title={pageTitle}
          width="100%"
          height={isNestedInNewPage && iframeHeight ? iframeHeight : undefined}
          scrolling="yes"
          className={cx(styles.LegacyIframe, {
            [styles.IsInModal]: isInModal,
            [styles.IsInNewNavigation]: hasNewNavigation,
            [styles.IsNestedInNewPage]: isNestedInNewPage,
          })}
          onLoad={handleIframeLoad}
          {...props}
          data-automation-id={props['data-automation-id']}
        />
      )}

      {isLoading && <LoadingSpinner isNestedInNewPage={isNestedInNewPage} />}
    </>
  );

  return isNestedInNewPage ? (
    <div className={styles.NestedInNewPageWrapper}>{content}</div>
  ) : (
    content
  );
};
